// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { AlertTriangle, ArrowUpRight } from 'lucide-react';

import { cn } from '@/lib/utils';

import { SettingsSectionHeading } from './SettingsSectionHeading';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DangerZoneProps {
  runbookUrl?: string;
  className?: string;
}

// ── Main component ────────────────────────────────────────────────────────────

export function DangerZone({ runbookUrl, className }: DangerZoneProps): React.JSX.Element {
  return (
    <div className={cn('space-y-6', className)}>
      <SettingsSectionHeading title="Danger zone" pill="Org admin" />

      {/* Red-bordered card — not a SectionCard; intentionally distinct */}
      <div
        className="rounded-gl-lg border border-gl-red/40 bg-gl-red/[0.04] overflow-hidden"
        role="region"
        aria-label="Danger zone"
      >
        {/* Body */}
        <div className="px-5 py-4">
          {/* Heading row: alert triangle + title */}
          <div className="flex items-start gap-2.5">
            <AlertTriangle
              className="mt-0.5 h-[18px] w-[18px] shrink-0 text-gl-red-text"
              aria-hidden="true"
            />
            <h3 className="font-semibold text-gl-red-text">Uninstall Greenlight</h3>
          </div>

          {/* Prose */}
          <p className="mt-1 text-sm text-muted-foreground">
            Greenlight is uninstalled from your own cloud — never from this dashboard. Removal is
            run by a cloud owner using the uninstall runbook, and it deletes everything Greenlight
            holds, the audit log included. Export the audit log and back up any data you want to
            keep first.
          </p>
        </div>

        {/* Footer bar */}
        <div className="flex items-center justify-between gap-3 border-t border-gl-red/30 px-5 py-3">
          {/* Left note */}
          <p className="text-xs text-muted-foreground">
            No action here removes anything. Apps and resources must be deleted individually first.
          </p>

          {/* Right: runbook CTA — anchor when URL available, disabled button otherwise */}
          {runbookUrl ? (
            <a
              href={runbookUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Open uninstall runbook (opens in new tab)"
              className="inline-flex h-11 md:h-9 shrink-0 items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              Open uninstall runbook
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
          ) : (
            <button
              type="button"
              disabled
              aria-label="Open uninstall runbook (unavailable)"
              className="inline-flex h-11 md:h-9 shrink-0 cursor-not-allowed items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium opacity-50 shadow-sm"
            >
              Open uninstall runbook
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
